import type { FiatPrice } from "@b2bportal/core-types";

import Big from "big.js";
import type { ExchangeRate, PointsExchange } from "./types";
import { Logger } from "@/utils/logging";

const log = new Logger("SimplePointsExchange");

// This is a PointsExchange based on a linear exchange rate.
export class SimplePointsExchange implements PointsExchange {
  exchangeRate: ExchangeRate;

  // A simple points exchange class that assumes a linear conversion rate.
  constructor(exchangeRate: ExchangeRate) {
    this.exchangeRate = exchangeRate;
  }

  pointsToFiat(points: number): FiatPrice {
    log.verbose("Converting points to fiat", {
      points,
      exchangeRate: this.exchangeRate,
    });

    // Is it necessary to multiply and divide by 100 still?
    // Probably not but it works
    const bigPoints = new Big(points);
    const bigExchangeRate = new Big(this.exchangeRate.value);
    const value = Number(
      bigPoints.times(bigExchangeRate).round(2, Big.roundDown).toString()
    );
    const fiat = { ...this.exchangeRate, value };

    log.verbose("Converted points to fiat", fiat);

    return fiat;
  }

  getPointsRequiredForFiat({ value }: { value: FiatPrice["value"] }): number {
    // Rounding so prices don't accidentally come in as 10.0000000001 or 9.9999999999
    const bigValue = new Big(value).round(2, Big.roundHalfUp);
    const bigExchangeRate = new Big(this.exchangeRate.value);
    return Number(
      bigValue.div(bigExchangeRate).round(0, Big.roundUp).toString()
    );
  }

  // We need to normalize the points applied to a value that will pass the
  // validation checks in the server. This is important because the value of a
  // point is less than 1 cent, so we there are some fiat values that represent
  // multiple point values.
  // To ensure the points applied is valid, we convert the points to fiat and
  // then back to points using the default points exchange.
  normalizePoints(points: number): number {
    const fiat = this.pointsToFiat(points);
    const normalizedPoints = this.getPointsRequiredForFiat(fiat);
    return normalizedPoints;
  }
}
